// Player-facing course catalog source. Resolves the BASE layer of courses the
// Play screen (and tournament picker) shows, in priority order:
//
//   1. The shared backend catalog (Cosmos) — authoritative, includes admin edits.
//   2. A localStorage cache of the last successful fetch — for offline use.
//   3. The static bundled seed (grouplooperCourses + mockCourses) — first-run /
//      never-been-online fallback, always present in the app bundle.
//
// CourseSelect then layers the player's own local edits/additions (coursesDb)
// on top of whatever this returns (mergeLocal), so a player still sees their
// in-progress changes — those stay local until admin-approved (moderation is a
// later phase). Since the catalog was seeded with the SAME ids as the static
// files, the DB base and the static fallback share ids — no duplication.

import { Course } from "./types";
import { mockCourses } from "./mockCourses";
import { grouplooperCourses } from "./grouplooperCourses";
import { queryCourses, getCourse } from "./courseCatalogApi";
import { loadCourses } from "./coursesDb";

const CACHE_KEY = "asksmart.caddiegames.catalogCache";

const STATIC_CATALOG: Course[] = [...mockCourses, ...grouplooperCourses];

// Synchronous best-guess catalog for the initial render — the cached fetch if
// present, else the bundled seed. Never blocks; fetchCatalog() refreshes after.
export function cachedCatalog(): Course[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    // corrupt / unavailable cache — fall through to the bundled seed
  }
  return STATIC_CATALOG;
}

// Fetch the live catalog, updating the offline cache on success. On any failure
// (offline, 403, 500) or an empty result, falls back to the cache/static seed
// so the player never lands on a blank course list.
export async function fetchCatalog(): Promise<Course[]> {
  try {
    const { courses: db } = await queryCourses({ limit: 200 });
    if (db.length) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(db));
      } catch {
        // quota / private mode — cache is best-effort
      }
      return db;
    }
    // Empty DB (not yet seeded, or a transient blip) — don't cache the empty
    // set; keep showing the last-known/bundled courses.
    return cachedCatalog();
  } catch {
    return cachedCatalog();
  }
}

// ── Player server-search (with offline fallback + local overlay) ───────────

interface SearchParams {
  q: string;
  country: string;
  region: string;
  offset: number;
  limit: number;
}

function matchesFilter(c: Course, p: SearchParams): boolean {
  if (p.country && c.country !== p.country) return false;
  if (p.region && c.region !== p.region) return false;
  if (p.q && !c.name.toLowerCase().includes(p.q.toLowerCase())) return false;
  return true;
}

// The player's course-search function for CourseSelect's server mode. Online it
// hits the backend (paginated + filtered); offline it filters the cached/bundled
// catalog. Either way it overlays the player's OWN local edits/additions
// (coursesDb) so their in-progress changes still show — those stay local until
// admin-approved.
export async function playerCourseSearch(
  p: SearchParams,
): Promise<{ courses: Course[]; hasMore: boolean }> {
  let base: Course[];
  let hasMore: boolean;
  try {
    const r = await queryCourses(p);
    base = r.courses;
    hasMore = r.hasMore;
  } catch {
    // Offline — filter the cached/bundled catalog client-side.
    const all = cachedCatalog()
      .filter((c) => matchesFilter(c, p))
      .sort((a, b) => a.name.localeCompare(b.name));
    base = all.slice(p.offset, p.offset + p.limit);
    hasMore = all.length > p.offset + p.limit;
  }

  const local = loadCourses();
  const localById = new Map(local.map((c) => [c.id, c] as const));
  // Replace catalog rows with the player's local override where one exists.
  const overlaid = base.map((c) => localById.get(c.id) ?? c);

  if (p.offset === 0) {
    // Prepend local-only additions (not in the catalog yet) that match the
    // filter — a course the player added shows in their own search.
    const baseIds = new Set(base.map((c) => c.id));
    const additions = local.filter(
      (c) => !baseIds.has(c.id) && matchesFilter(c, p),
    );
    return { courses: [...additions, ...overlaid], hasMore };
  }
  return { courses: overlaid, hasMore };
}

// Facet options for the player's Advanced-search dropdowns. Online → backend;
// offline → derive from the cached/bundled catalog.
export async function playerLoadFacets(
  country: string,
): Promise<{ countries: string[]; regions: string[] }> {
  try {
    const { getCourseFacets } = await import("./courseCatalogApi");
    return await getCourseFacets(country);
  } catch {
    const all = cachedCatalog();
    const countries = [
      ...new Set(all.map((c) => c.country).filter(Boolean)),
    ].sort() as string[];
    const src = country ? all.filter((c) => c.country === country) : all;
    const regions = [
      ...new Set(src.map((c) => c.region).filter(Boolean)),
    ].sort() as string[];
    return { countries, regions };
  }
}

// ── Favourite-course cache (server mode has no whole-catalog to resolve ids) ─

const FAV_CACHE_KEY = "asksmart.caddiegames.favoriteCourseCache";

function readFavCache(): Record<string, Course> {
  try {
    const raw = localStorage.getItem(FAV_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    // ignore
  }
  return {};
}

// Remember a course's data when it's favourited, so the strip can render it
// (name/location) without holding the whole catalog.
export function cacheFavoriteCourse(course: Course): void {
  const cache = readFavCache();
  cache[course.id] = course;
  try {
    localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // best effort
  }
}

// Resolve favourite ids to course objects: cache first, then fetch any missing
// by id (e.g. favourited on another device), then the bundled/cached catalog.
// Preserves favourite order; ids that can't resolve drop out.
export async function resolveFavoriteCourses(
  ids: string[],
): Promise<Course[]> {
  const cache = readFavCache();
  const bundledById = new Map(cachedCatalog().map((c) => [c.id, c] as const));
  const out = new Map<string, Course>();
  let cacheDirty = false;
  for (const id of ids) {
    if (cache[id]) {
      out.set(id, cache[id]);
      continue;
    }
    try {
      const c = await getCourse(id);
      cache[id] = c;
      cacheDirty = true;
      out.set(id, c);
    } catch {
      const b = bundledById.get(id);
      if (b) out.set(id, b);
    }
  }
  if (cacheDirty) {
    try {
      localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(cache));
    } catch {
      // best effort
    }
  }
  return ids.map((id) => out.get(id)).filter((c): c is Course => !!c);
}
